/**
 * CLI OAuth login page — /cli-login/:sessionId
 *
 * User signs in with GitLab (existing SignInPage), then this page completes the
 * CLI session and displays access + refresh tokens for copy / script polling.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import FileCopyOutlined from '@material-ui/icons/FileCopyOutlined';
import Check from '@material-ui/icons/Check';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { Content, Header, Page, InfoCard } from '@backstage/core-components';
import {
  useApi,
  configApiRef,
  fetchApiRef,
  identityApiRef,
  type IdentityApi,
} from '@backstage/core-plugin-api';
import { clearOAuthReloadFlag } from '../../utils/postOAuthReload';

type SessionPoll = {
  status: 'pending' | 'ready' | 'denied' | 'expired';
  usernameHint?: string;
  userEntityRef?: string;
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  error?: string;
  message?: string;
};

type CompleteResponse = {
  status: string;
  userEntityRef?: string;
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  error?: string;
  message?: string;
};

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * After SignInPage hands off, identity/credentials can lag briefly (esp. incognito).
 * Wait until both are available before calling /complete.
 */
async function waitForSignedInIdentity(
  identityApi: IdentityApi,
): Promise<{ userEntityRef: string; token?: string }> {
  let lastError: unknown;
  for (let attempt = 0; attempt < 12; attempt++) {
    if (attempt > 0) {
      await sleep(150 * attempt);
    }
    try {
      const identity = await identityApi.getBackstageIdentity();
      const { token } = await identityApi.getCredentials();
      if (identity.userEntityRef) {
        return { userEntityRef: identity.userEntityRef, token };
      }
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new Error('Signed-in session is not ready yet. Retry in a moment.');
}

function TokenField(props: {
  label: string;
  value: string;
  helperText?: string;
}) {
  const [copied, setCopied] = useState(false);
  const [visible, setVisible] = useState(false);
  const onCopy = async () => {
    const ok = await copyText(props.value);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <Box mb={2}>
      <Typography variant="subtitle2" gutterBottom>
        {props.label}
      </Typography>
      <TextField
        fullWidth
        variant="outlined"
        type={visible ? 'text' : 'password'}
        value={props.value}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={visible ? `Hide ${props.label}` : `Show ${props.label}`}
                onClick={() => setVisible(v => !v)}
                edge="end"
                size="small"
              >
                {visible ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        helperText={
          visible
            ? props.helperText
            : 'Hidden — click the eye icon to reveal'
        }
      />
      <Box mt={1}>
        <Button
          size="small"
          color="primary"
          variant="outlined"
          startIcon={copied ? <Check /> : <FileCopyOutlined />}
          onClick={onCopy}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </Box>
    </Box>
  );
}

export const CliLoginPage = () => {
  const { sessionId = '' } = useParams<{ sessionId: string }>();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const completedOnce = useRef(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usernameHint, setUsernameHint] = useState<string>('');
  const [signedInAs, setSignedInAs] = useState<string>('');
  const [accessToken, setAccessToken] = useState<string>('');
  const [refreshToken, setRefreshToken] = useState<string>('');
  const [expiresIn, setExpiresIn] = useState<number | undefined>();
  const [userEntityRef, setUserEntityRef] = useState<string>('');

  const backendBaseUrl = configApi.getString('backend.baseUrl').replace(/\/$/, '');

  const applyTokens = useCallback(
    (tokens: {
      accessToken: string;
      refreshToken: string;
      expiresIn?: number;
      userEntityRef?: string;
      fallbackUserEntityRef: string;
    }) => {
      setAccessToken(tokens.accessToken);
      setRefreshToken(tokens.refreshToken);
      setExpiresIn(tokens.expiresIn);
      setUserEntityRef(tokens.userEntityRef || tokens.fallbackUserEntityRef);
      setError(null);
      completedOnce.current = true;
      clearOAuthReloadFlag();
      try {
        sessionStorage.removeItem(
          `iron-phoenix.cli-login.complete-reload:${sessionId}`,
        );
      } catch {
        /* ignore */
      }
    },
    [sessionId],
  );

  /** One automatic full reload if /complete keeps returning 401 after redirect. */
  const reloadOnceAfterCompleteAuthLag = useCallback((): boolean => {
    const key = `iron-phoenix.cli-login.complete-reload:${sessionId}`;
    try {
      if (sessionStorage.getItem(key) === '1') {
        return false;
      }
      sessionStorage.setItem(key, '1');
    } catch {
      return false;
    }
    window.location.reload();
    return true;
  }, [sessionId]);

  const completeSession = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!sessionId.trim()) {
        setError('Missing session id in URL.');
        return;
      }

      const identity = await waitForSignedInIdentity(identityApi);
      setSignedInAs(identity.userEntityRef);

      const metaRes = await fetchApi.fetch(
        `${backendBaseUrl}/api/cli-auth/session/${encodeURIComponent(sessionId)}`,
      );
      const meta = (await metaRes.json()) as SessionPoll;
      if (meta.usernameHint) {
        setUsernameHint(meta.usernameHint);
      }
      if (!metaRes.ok && metaRes.status === 404) {
        setError(meta.error || 'Unknown CLI auth session. Start a new login from the CLI.');
        return;
      }
      if (meta.status === 'denied' || meta.status === 'expired') {
        setError(
          meta.status === 'denied'
            ? 'This CLI login was denied (username mismatch or catalog check failed). Start a new login.'
            : 'This CLI login session expired. Start a new login from the CLI.',
        );
        return;
      }
      if (meta.status === 'ready' && meta.accessToken && meta.refreshToken) {
        applyTokens({
          accessToken: meta.accessToken,
          refreshToken: meta.refreshToken,
          expiresIn: meta.expiresIn,
          userEntityRef: meta.userEntityRef,
          fallbackUserEntityRef: identity.userEntityRef,
        });
        return;
      }
      if (meta.status === 'ready') {
        // Tokens may already have been delivered to the CLI poller; keep a short
        // retry window in case the browser lost the race with the first poll.
        for (let i = 0; i < 5; i++) {
          await sleep(400);
          const retryRes = await fetchApi.fetch(
            `${backendBaseUrl}/api/cli-auth/session/${encodeURIComponent(sessionId)}`,
          );
          const retry = (await retryRes.json()) as SessionPoll;
          if (retry.accessToken && retry.refreshToken) {
            applyTokens({
              accessToken: retry.accessToken,
              refreshToken: retry.refreshToken,
              expiresIn: retry.expiresIn,
              userEntityRef: retry.userEntityRef,
              fallbackUserEntityRef: identity.userEntityRef,
            });
            return;
          }
        }
        setUserEntityRef(meta.userEntityRef || identity.userEntityRef);
        setError(
          meta.message ||
            'Authorization completed for the CLI, but tokens are no longer shown here. Check your terminal — credentials were likely already saved.',
        );
        return;
      }

      // Retry /complete — first call often 401 right after SignInPage hands off.
      let body: CompleteResponse | undefined;
      let completeRes: Response | undefined;
      for (let attempt = 0; attempt < 8; attempt++) {
        if (attempt > 0) {
          await sleep(200 * attempt);
        }
        const { token } =
          attempt === 0
            ? { token: identity.token }
            : await identityApi.getCredentials();

        completeRes = await fetchApi.fetch(
          `${backendBaseUrl}/api/cli-auth/complete`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ sessionId }),
          },
        );
        body = (await completeRes.json()) as CompleteResponse;

        if (completeRes.ok && body.accessToken && body.refreshToken) {
          applyTokens({
            accessToken: body.accessToken,
            refreshToken: body.refreshToken,
            expiresIn: body.expiresIn,
            userEntityRef: body.userEntityRef,
            fallbackUserEntityRef: identity.userEntityRef,
          });
          return;
        }

        // Transient auth / readiness failures — keep trying instead of forcing refresh.
        if (completeRes.status === 401 || completeRes.status === 503) {
          continue;
        }
        break;
      }

      if (!completeRes || !body) {
        setError('Complete failed — no response from server.');
        return;
      }
      if (!completeRes.ok) {
        if (
          completeRes.status === 401 &&
          reloadOnceAfterCompleteAuthLag()
        ) {
          return;
        }
        setError(body.error || `Complete failed (${completeRes.status})`);
        return;
      }
      if (body.accessToken && body.refreshToken) {
        applyTokens({
          accessToken: body.accessToken,
          refreshToken: body.refreshToken,
          expiresIn: body.expiresIn,
          userEntityRef: body.userEntityRef,
          fallbackUserEntityRef: identity.userEntityRef,
        });
      } else {
        setError(body.message || 'Complete succeeded but no tokens were returned.');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (
        /not ready|authentication required|401/i.test(msg) &&
        reloadOnceAfterCompleteAuthLag()
      ) {
        return;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [
    applyTokens,
    backendBaseUrl,
    fetchApi,
    identityApi,
    reloadOnceAfterCompleteAuthLag,
    sessionId,
  ]);

  useEffect(() => {
    if (completedOnce.current) {
      return;
    }
    void completeSession();
  }, [completeSession]);

  return (
    <Page themeId="tool">
      <Header
        title="CLI authorization"
        subtitle="Authorize Iron Phoenix API access for curl / scripts"
      />
      <Content>
        <InfoCard title="CLI login">
          {loading && (
            <Box display="flex" alignItems="center" p={2}>
              <CircularProgress size={24} style={{ marginRight: 12 }} />
              <Typography>Completing CLI authorization…</Typography>
            </Box>
          )}

          {!loading && error && (
            <Box p={2}>
              <Typography color="error" paragraph>
                {error}
              </Typography>
              <Button variant="outlined" color="primary" onClick={() => void completeSession()}>
                Retry
              </Button>
            </Box>
          )}

          {!loading && !error && accessToken && refreshToken && (
            <Box p={1}>
              <Typography paragraph>
                Authorization successful
                {usernameHint ? (
                  <>
                    {' '}
                    for requested user <strong>{usernameHint}</strong>
                  </>
                ) : null}
                {signedInAs || userEntityRef ? (
                  <>
                    {' '}
                    (signed in as <strong>{userEntityRef || signedInAs}</strong>)
                  </>
                ) : null}
                .
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Tokens are shown below (hidden by default). Your CLI also saves them
                automatically when login finishes.
                {expiresIn
                  ? ` Access token expires in about ${Math.round(expiresIn / 60)} minutes.`
                  : ' Access token expires in about 1 hour.'}
              </Typography>

              <TokenField
                label="Access token"
                value={accessToken}
                helperText="Authorization: Bearer <access_token>"
              />
              <TokenField
                label="Refresh token"
                value={refreshToken}
                helperText="POST /api/cli-auth/refresh { refreshToken }"
              />

              <Paper variant="outlined">
                <Box p={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Example curl
                  </Typography>
                  <Typography
                    component="pre"
                    style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}
                  >
                    {`curl -sS -H "Authorization: Bearer <access_token>" \\\n  "${backendBaseUrl}/api/catalog/entities?limit=1"`}
                  </Typography>
                </Box>
              </Paper>
            </Box>
          )}
        </InfoCard>
      </Content>
    </Page>
  );
};
